import React from 'react';
import {
  Pulse,
  TreeStructure,
  CheckCircle,
  XCircle,
  Clock,
  MinusCircle,
  Spinner,
} from '@phosphor-icons/react';
import { useWorkflowRuns, type WorkflowRunStatus } from '../hooks/useWorkflowRuns';

type WorkflowSummaryCardProps = {
  workflowId: string | null;
  nodeCount: number;
  onSwitchToRunsTab?: () => void;
};

const statusConfig: Record<
  WorkflowRunStatus,
  { label: string; icon: React.ReactNode; className: string }
> = {
  success: {
    label: 'Passed',
    icon: <CheckCircle size={13} weight="fill" />,
    className: 'text-success',
  },
  failed: {
    label: 'Failed',
    icon: <XCircle size={13} weight="fill" />,
    className: 'text-destructive',
  },
  inconclusive: {
    label: 'Platform error',
    icon: <MinusCircle size={13} />,
    className: 'text-muted-foreground',
  },
  timeout: {
    label: 'Timeout',
    icon: <Clock size={13} weight="fill" />,
    className: 'text-destructive',
  },
  running: { label: 'Running', icon: <Spinner size={13} />, className: 'text-warning' },
  queued: { label: 'Queued', icon: <Clock size={13} />, className: 'text-muted-foreground' },
  canceled: {
    label: 'Canceled',
    icon: <MinusCircle size={13} />,
    className: 'text-muted-foreground',
  },
};

export const WorkflowSummaryCard: React.FC<WorkflowSummaryCardProps> = ({
  workflowId,
  nodeCount,
  onSwitchToRunsTab,
}) => {
  const { data: runsData } = useWorkflowRuns(workflowId, {
    page: 1,
    pageSize: 1,
  });

  const totalRuns = runsData?.pagination?.totalItems ?? 0;
  const lastRun = runsData?.data?.runs?.[0] ?? null;
  const status = lastRun ? statusConfig[lastRun.status] : null;

  return (
    <div className="mx-4 mt-4 flex items-center gap-3 rounded-md bg-muted/50 px-3 py-2 text-xs">
      <button
        type="button"
        onClick={onSwitchToRunsTab}
        className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors"
      >
        <Pulse size={13} />
        <span className="font-semibold text-foreground">{totalRuns}</span>
        <span>{totalRuns === 1 ? 'run' : 'runs'}</span>
      </button>

      <span className="text-border">·</span>

      <span className="flex items-center gap-1.5 text-muted-foreground">
        <TreeStructure size={13} />
        <span className="font-semibold text-foreground">{nodeCount}</span>
        <span>{nodeCount === 1 ? 'node' : 'nodes'}</span>
      </span>

      {status && (
        <>
          <span className="text-border">·</span>
          <span className="flex items-center gap-1.5 text-muted-foreground">
            <span>Last run</span>
            <span className={`flex items-center gap-1 ${status.className}`}>
              {status.icon}
              <span className="font-medium">{status.label}</span>
            </span>
          </span>
        </>
      )}
    </div>
  );
};
